import React, { useState, useEffect, useRef } from "react";
import TextInput from "../FormItems/TextInput.jsx";
import SelectInput from "../FormItems/SelectInput.jsx";
import MultiSelect from "./components/MultiSelect.jsx";
import LoadingScreen from "./components/LoadingScreen.jsx";
 
import { states, stateToAbbreviation } from "./Utility/state.js";
import { validateData, isFieldHidden, PHONE_REGEX } from "./SSORequestAccountFormValidator.js";
import { toast, ToastContainer } from "react-toastify";
import ToastAlert, { ToastCloseIcon } from "../ToastAlert/ToastAlert.jsx";
import FormSubmitted from "./components/FormSubmitted.jsx";
 
const ZIP_URL = "/api/SmartyAPI/GetAddressAsync";
const REGISTER_URL =
  "/api/B2C/UserRegistrationRequest/UserRegistrationRequestAsync";
const UPDATE_PROFILE_URL =
  "/api/B2C/UserRegistrationRequest/UserRegistrationUpdateAsync";
 
function SSORequestAccountForm({
  mode = "register", // "register" | "edit"
  initialData = null, // Pre-fill data for edit mode
  variant = "", // Site variant used to opt fields out of the UI/validation (e.g. "stewartcom")
}) {
  const formRef = useRef(null); // create a reference for the form
  const isHidden = field => isFieldHidden(field, variant);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [submitResult, setSubmitResult] = useState(null); // null, "success", "pending", "active", "inactive"
  const [selectedStates, setSelectedStates] = useState({});
  const [businessState, setBusinessState] = useState([]);
  const [errors, setErrors] = useState({});
  const [smsConsentEnabled, setSmsConsentEnabled] = useState(false);
  const [smsConsentGiven, setSmsConsentGiven] = useState(false);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [isStewartEmployee, setIsStewartEmployee] = useState(false);
 
  const GENERIC_TOAST_SETTINGS = {
    className: "st-toast-alert",
    hideProgressBar: true,
    position: "bottom-right",
    closeButton: <ToastCloseIcon />,
  };
  // Form field state for controlled components
  const [formData, setFormData] = useState({
    FirstName: "",
    LastName: "",
    Email: "",
    OfficePhone: "",
    MobilePhone: "",
    CompanyName: "",
    BusinessLine: "",
    StewartContact: "",
    StewartContactEmail: "",
    NewsLetterSignUp: false,
  });
 
  const [zipCodeValue, setZipCodeValue] = useState("");
  const [city, setCity] = useState({
    city: "",
    state: "",
  });
  const [retrievedCityState, setRetrievedCityState] = useState("");
 
  const checkFormValidity = () => {
    if (!formRef.current) return false;
    const fd = new FormData(formRef.current);
    const FirstName = (fd.get("FirstName") || "").trim();
    const LastName = (fd.get("LastName") || "").trim();
    // Email addresses cannot be edited in Edit mode, so since they've already
    // passed validation during registration, we can consider them valid here.
    const Email = mode === "register" ? (fd.get("Email") || "").trim() : true;
    const ZipCode = (fd.get("ZipCode") || "").trim();
    const CompanyName = isHidden("CompanyName")
      ? true
      : (fd.get("CompanyName") || "").trim();
    // Business Line cannot be edited in Edit mode, so bypass validation.
    const BusinessLine = isHidden("BusinessLine")
      ? true
      : mode === "register"
        ? (fd.get("BusinessLine") || "").trim()
        : true;
 
    if (mode === "register" && typeof Email === "string") {
      const isStewartEmployee = Email.toLowerCase().endsWith("@stewart.com");
      setIsStewartEmployee(isStewartEmployee);
    }
 
    const isBlockedStewart = mode === "register" && typeof Email === "string" &&
      Email.toLowerCase().endsWith("@stewart.com");
    const cityOk = !!city?.city && !!city?.state;
 
    const StewartContact = isHidden("StewartContact")
      ? true
      : (fd.get("StewartContact") || "").trim();
    const StewartContactEmail = isHidden("StewartContactEmail")
      ? true
      : (fd.get("StewartContactEmail") || "").trim();

    const noErrors = !(
      errors.FirstName ||
      errors.LastName ||
      errors.Email ||
      errors.ZipCode ||
      (!isHidden("CompanyName") && errors.CompanyName) ||
      (!isHidden("BusinessLine") && errors.BusinessLine) ||
      errors.City || (mode === "register" && (
        (!isHidden("StewartContact") && errors.StewartContact) ||
        (!isHidden("StewartContactEmail") && errors.StewartContactEmail)
      ))
    );

    const allPresent =
      FirstName && LastName && Email && ZipCode && CompanyName && BusinessLine && (mode === "edit" || (StewartContact && StewartContactEmail));
 
    return allPresent && cityOk && noErrors && !isBlockedStewart;
  };
 
  useEffect(() => {
    // Load business units from global model
    if (window?.regFormModel?.businessUnits) {
      setBusinessUnits(
        window.regFormModel.businessUnits.reduce((acc, unit) => {
          acc.push({ text: unit });
          return acc;
        }, []),
      );
    }
  }, []);
 
  // Populate form with initial data when in edit mode
  useEffect(() => {
    if (mode === "edit" && initialData) {
      // Set form data
      setFormData({
        FirstName: initialData.FirstName || "",
        LastName: initialData.LastName || "",
        Email: initialData.Email || "",
        OfficePhone: initialData.OfficePhone || "",
        MobilePhone: initialData.MobilePhone || "",
        CompanyName: initialData.CompanyName || "",
        BusinessLine: initialData.BusinessLine || "",
        StewartContact: initialData.StewartContact || "",
        StewartContactEmail: initialData.StewartContactEmail || "",
        NewsLetterSignUp: initialData.NewsLetterSignUp || false,
      });
 
      if (initialData.ZipCode) {
        setZipCodeValue(initialData.ZipCode);
      }
      if (initialData.City && initialData.State) {
        setCity({
          city: initialData.City,
          state: initialData.State,
        });
        setRetrievedCityState(`${initialData.City}, ${initialData.State}`);
      }
      if (initialData.DoingBusinessIn) {
        // Parse comma-separated states back into selectedStates object
        const statesArray = initialData.DoingBusinessIn.split(", ").map(s =>
          s.trim(),
        );
        const statesObj = {};
        statesArray.forEach(stateName => {
          // Use state abbreviation as key to match the MultiSelect's option.value
          const abbreviation = stateToAbbreviation[stateName];
          if (abbreviation) {
            statesObj[abbreviation] = stateName;
          }
        });
        setSelectedStates(statesObj);
      }
      // SMS consent checkbox should be enabled if valid mobile number is present
      // not BUT necessarily checked
      if (
        initialData.MobilePhone &&
        PHONE_REGEX.test(initialData.MobilePhone)
      ) {
        setSmsConsentEnabled(true);
        // Set SMS consent given if previously consented
        // Requires a valid mobile number
        if (initialData.SMSMessage) {
          setSmsConsentGiven(initialData.SMSMessage);
        }
      }
    }
  }, [mode, initialData]);
 
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (zipCodeValue) {
        fetchZipCode();
      }
    }, 500); // 500ms debounce
 
    return () => clearTimeout(debounceTimer);
  }, [zipCodeValue]);
 
  useEffect(() => {
    setCanSubmit(checkFormValidity());
  }, [city, errors]);
 
  const handleFieldChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };
 
  const handleChange = e => {
    const {
      target: { value },
    } = e;
 
    setBusinessState(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value,
    );
    setCanSubmit(checkFormValidity());
  };
 
  const handleSubmit = async e => {
    e.preventDefault();
    // Gather form data
    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData.entries());
    let payload = {
      ...data,
      City: city?.city,
      State: stateToAbbreviation[city?.state] || "",
      DoingBusinessIn: selectedStates
        ? Object.values(selectedStates)
          .map(v => (typeof v === "string" ? v : v?.label))
          .join(", ")
        : undefined,
      NewsLetterSignUp: data.NewsLetterSignUp === "on",
      SMSMessage: smsConsentGiven,
      OfficePhone:
        data.OfficePhone !== ""
          ? data.OfficePhone.replace(/\D/g, "")
          : undefined,
      MobilePhone:
        data.MobilePhone !== ""
          ? data.MobilePhone.replace(/\D/g, "")
          : undefined,
    };
 
    const email = (data.Email || "").toLowerCase();
 
    if (mode === "register" && email.endsWith("@stewart.com")) {
      toast(
        <ToastAlert
          style="error"
          title="Stewart Employee Login Required"
          description="Please log in using the Stewart Employee Login."
        />,
        GENERIC_TOAST_SETTINGS
      );
      return;
    }
 
    if (mode === "edit") {
      // For edit mode, prepare payload according to new API spec
      payload = {
        ...payload,
        Email: initialData?.Email, // field is disabled, so use initial value
        JobTitle: "User", // Default value per API spec
      };
    }
 
    if (mode === "register") {
      if (!isHidden("StewartContact") && !payload.StewartContact?.trim()) {
        setErrors(prev => ({
          ...prev,
          StewartContact: "Stewart Contact is required."
        }));
        formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      if (!isHidden("StewartContactEmail") && !payload.StewartContactEmail?.trim()) {
        setErrors(prev => ({
          ...prev,
          StewartContactEmail: "Stewart Contact Email is required."
        }));
        formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    // Run custom validation (replaces zod schema)
    const { valid, errors: validationErrors } = validateData(payload, mode, variant);
 
    if (!valid) {
      setErrors(validationErrors);
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      return; // Abort submit if invalid
    }
    setErrors({});
 
    // Submit to API
    try {
      setIsSubmitting(true);
 
      // Determine API endpoint based on mode
      const apiUrl = mode === "register" ? REGISTER_URL : UPDATE_PROFILE_URL;
 
      // Site Key owned by Stewart Title
      // As of August 2025, was not configured to work with localhost requests
      // so this will never submit successfully in Storybook.
      const SITE_KEY = `6Lc8rwYaAAAAAGsJKqZhD-FjPHtuq1D56kx47AnM`;
 
      const { grecaptcha } = window;
 
      const grecaptchaToken = await grecaptcha.execute(SITE_KEY, {
        action: "submit",
      });
 
      if (grecaptchaToken) {
        payload.GoogleCaptchaToken = grecaptchaToken;
        if (variant) {
          payload.Variant = variant;
        }

        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
 
        if (response.ok) {
          // Response ok / 200
          if (mode === "edit") {
            // In edit mode, show success toast instead of success view
            // Timeout improves reliability of toast display
            setTimeout(() => {
              toast(
                <ToastAlert
                  style="success"
                  title="Success"
                  description="Your profile has been updated successfully."
                />,
                GENERIC_TOAST_SETTINGS,
              );
            }, 200);
          } else {
            // In register mode, show success view
            setSubmitResult("success");
          }
          setIsSubmitting(false);
        } else {
          // Response NOT ok / 200
          if (mode === "edit") {
            // Edit mode: 404 = invalid JSON, captcha verification failed, or user doesn't exist
            if (response.status === 404) {
              setIsSubmitting(false);
              setTimeout(() => {
                toast(
                  <ToastAlert
                    style="error"
                    title="Error"
                    description="Unable to update profile. Please verify your information and try again."
                  />,
                  GENERIC_TOAST_SETTINGS,
                );
              }, 200);
              // Edit mode: 500 = update failed
            } else if (response.status === 500) {
              setIsSubmitting(false);
              setTimeout(() => {
                toast(
                  <ToastAlert
                    style="error"
                    title="Error"
                    description="Profile update failed. Please try again later."
                  />,
                  GENERIC_TOAST_SETTINGS,
                );
              }, 200);
            }
            // Register mode
          } else {
            if (response.status === 422) {
              // 422 = existing account, check status
              const result = await response.json();
              const existingStatus = result?.Status ?? result?.status;
              if (existingStatus === 1) {
                setSubmitResult("active");
              } else if (existingStatus === 3 || existingStatus === 4) {
                // inactive (3) or rejected (4) should display same content (STSSO-94)
                setSubmitResult("inactive");
              } else {
                // pending
                setSubmitResult("pending");
              }
            } else {
              console.log("Response not ok:", response);
              let err = new Error(
                `HTTP ${response.status}: ${response.statusText}`,
              );
              err.response = response;
              err.status = response.status;
              throw err;
            }
          }
          setIsSubmitting(false);
        }
      } else {
        throw new Error("reCAPTCHA token not obtained");
      }
    } catch (error) {
      setIsSubmitting(false);
      if (error?.response?.status === 409) {
        toast.warn(error?.response?.data);
      } else {
        setSubmitResult(null);
        toast(
          <ToastAlert
            style="error"
            title="Error"
            description={
              error?.response?.data ||
              "An unexpected error occurred. Please try again."
            }
          />,
          GENERIC_TOAST_SETTINGS,
        );
      }
    }
  };
 
  const resetErrors = e => {
    const { name, value, type, checked } = e.target;
 
    // Update form data
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
 
    // Clear error for this field
    if (errors[name]) {
      setErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }
    setCanSubmit(checkFormValidity());
  };
 
  const handleClear = e => {
    formRef.current.reset();
    setFormData({
      FirstName: "",
      LastName: "",
      Email: "",
      OfficePhone: "",
      MobilePhone: "",
      CompanyName: "",
      BusinessLine: "",
      StewartContact: "",
      StewartContactEmail: "",
      NewsLetterSignUp: false,
    });
    setBusinessState([]);
    setZipCodeValue("");
    setCity({
      city: "",
      state: "",
    });
    setRetrievedCityState("");
    setSelectedStates({});
    setErrors({});
    setCanSubmit(false);
    setNoStewartContact(false);
  };
 
  const handleChangeZipCode = e => {
    resetErrors(e);
    resetErrors({ target: { name: "City" } });
    setZipCodeValue(e.target.value);
  };
 
  const handleMobileChange = e => {
    resetErrors(e);
    if (e.target.value !== "" && PHONE_REGEX.test(e.target.value)) {
      setSmsConsentEnabled(true);
    } else {
      setSmsConsentEnabled(false);
      setSmsConsentGiven(false);
    }
  };
 
  const fetchZipCode = async () => {
    if (!zipCodeValue || zipCodeValue.length < 5) return;
    setRetrievedCityState("Loading...");
    fetch(ZIP_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        requestAddress: [{ zipcode: zipCodeValue }],
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json(); // Parse the JSON response
      })
      .then(data => {
        if (data.city === "" && data.state === "") {
          setTimeout(() => {
            toast(
              <ToastAlert
                style="warning"
                title="Error"
                description={`No location found for ${zipCodeValue}. Please enter a valid ZIP code.`}
              />,
              {
                className: "st-toast-alert",
                hideProgressBar: true,
                position: "bottom-right",
                closeButton: <ToastCloseIcon />,
              },
            );
          }, 200);
        }
        setCity(data);
        setRetrievedCityState(
          data.city && data.state ? `${data.city}, ${data.state}` : "",
        );
      })
      .catch(error => {
        console.error("Failed to fetch zip code data:", error);
        toast(
          <ToastAlert
            style="error"
            title="Error"
            description={
              error?.response?.data ||
              "Failed to fetch ZIP code data. Please try again."
            }
          />,
          {
            className: "st-toast-alert",
            hideProgressBar: true,
            position: "bottom-right",
            closeButton: <ToastCloseIcon />,
          },
        );
        setCity({ city: "", state: "" });
        setRetrievedCityState("");
      });
  };
 
  return (
    <>
      <div className="request-account-form">
        {!submitResult && (
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            key={mode === "edit" && initialData ? "edit-mode" : "register-mode"}
          >
            <div className="request-account-form__grid">
              <div
                className="request-account-form__grid-col"
                style={{ "--span": 8 }}
              >
                <TextInput
                  label="*First Name"
                  name="FirstName"
                  error={!!errors.FirstName}
                  errorMsg={errors.FirstName}
                  required={true}
                  onChange={resetErrors}
                  value={formData.FirstName}
                />
              </div>
              <div
                className="request-account-form__grid-col"
                style={{ "--span": 8 }}
              >
                <TextInput
                  label="*Last Name"
                  name="LastName"
                  error={!!errors.LastName}
                  errorMsg={errors.LastName}
                  required={true}
                  onChange={resetErrors}
                  value={formData.LastName}
                />
              </div>
              <div
                className="request-account-form__grid-col"
                style={{ "--span": 5 }}
              >
                <TextInput
                  label="*Email"
                  name="Email"
                  type="text"
                  required={true}
                  error={!!errors.Email}
                  errorMsg={errors.Email}
                  onChange={resetErrors}
                  value={formData.Email}
                  disabled={mode === "edit"}
                />
                {isStewartEmployee && mode === "register" && (
                  <div className="employee-hint error-message">
                    <span className="error-icon">⚠️</span>
                    <p className="error-text">Looks like you’re using an <strong>@stewart.com</strong> email.
                      Please log in using the <strong>Stewart Employee Login</strong>.
                    </p>
                  </div>
                )}
              </div>
              {!isHidden("NewsLetterSignUp") && (
                <div
                  className="request-account-form__grid-col"
                  style={{ "--span": 3 }}
                >
                  <label className="request-account-form__checkbox">
                    <input
                      name="NewsLetterSignUp"
                      type="checkbox"
                      checked={formData.NewsLetterSignUp}
                      onChange={resetErrors}
                    />{" "}
                    Sign me up for newsletter
                  </label>
                </div>
              )}
              <div
                className="request-account-form__grid-col"
                style={{ "--span": 2 }}
              >
                <TextInput
                  label="Office Number"
                  name="OfficePhone"
                  error={!!errors.OfficePhone}
                  errorMsg={errors.OfficePhone}
                  value={formData.OfficePhone}
                  onChange={resetErrors}
                />
              </div>
              <div
                className="request-account-form__grid-col"
                style={{ "--span": 2 }}
              >
                <TextInput
                  label="Mobile Number"
                  name="MobilePhone"
                  onChange={handleMobileChange}
                  error={!!errors.MobilePhone}
                  errorMsg={errors.MobilePhone}
                  value={formData.MobilePhone}
                />
              </div>
              <div
                className="request-account-form__grid-col"
                style={{ "--span": 4 }}
              >
                <label className="request-account-form__checkbox">
                  {/*
                    NOTE - DO NOT CHECK BY DEFAULT.
                    USERS *MUST* PROVIDE EXPRESS CONSENT TO SMS MESSAGES
                  */}
                  <input
                    type="checkbox"
                    disabled={!smsConsentEnabled}
                    checked={smsConsentGiven}
                    name="SMSMessage"
                    onChange={e => setSmsConsentGiven(e.target.checked)}
                  />
                  I give my consent to SMS messages
                </label>
              </div>
              <div
                className="request-account-form__grid-col"
                style={{ "--span": 4 }}
              >
                <TextInput
                  label="*ZIP Code"
                  name="ZipCode"
                  onChange={handleChangeZipCode}
                  error={!!errors.ZipCode}
                  errorMsg={errors.ZipCode}
                  required={true}
                  value={zipCodeValue}
                />
              </div>
              <div
                className="request-account-form__grid-col"
                style={{ "--span": 12 }}
              >
                <TextInput
                  disabled={true}
                  placeholder="Please enter a ZIP code"
                  value={retrievedCityState}
                  name="city"
                  error={!!errors.City}
                  errorMsg={errors.City}
                />
              </div>
              {!isHidden("CompanyName") && (
                <div
                  className="request-account-form__grid-col"
                  style={{ "--span": mode === "edit" ? 4 : 8 }}
                >
                  <TextInput
                    name="CompanyName"
                    label="*Company Name"
                    error={!!errors.CompanyName}
                    errorMsg={errors.CompanyName}
                    required={true}
                    onChange={resetErrors}
                    value={formData.CompanyName}
                  />
                </div>
              )}
              {mode === "register" && !isHidden("BusinessLine") && (
                <div
                  className="request-account-form__grid-col"
                  style={{ "--span": 8 }}
                >
                  <SelectInput
                    label="*Business Line"
                    name="BusinessLine"
                    options={businessUnits}
                    error={!!errors.BusinessLine}
                    errorMsg={errors.BusinessLine}
                    required={true}
                    onChange={e => {
                      handleChange(e);
                      handleFieldChange(e);
                    }}
                    value={formData.BusinessLine}
                  />
                </div>
              )}
 
              <div
                className="request-account-form__grid-col"
                style={{ "--span": 4 }}
              >
                <label className={"sc-form-item"}>
                  <span className="sc-form-item__label">Doing Business In</span>
                  <MultiSelect
                    options={states}
                    selectedValues={selectedStates}
                    setSelectedValues={setSelectedStates}
                    placeholder="Select States"
                    // callback={selected => {
                    //   setSelectedStates(selected);
                    // }}
                    name="DoingBusinessIn"
                  />
                </label>
              </div>
              <div
                className="request-account-form__grid-col"
                style={{ "--span": mode === "edit" ? 8 : 12 }}
              >
                <TextInput
                  value={
                    Object.values(selectedStates).length > 0
                      ? Object.values(selectedStates)
                        .map(v => (typeof v === "string" ? v : v?.label))
                        .join(", ")
                      : "Please select a state"
                  }
                  disabled={true}
                  name="businessState"
                />
              </div>
              {mode === "register" && !isHidden("StewartContact") && !isHidden("StewartContactEmail") && (
                <>
                  <div
                    className="request-account-form__grid-col"
                    style={{ "--span": 8 }}
                  >
                    <TextInput
                      label="*Stewart Contact"
                      name="StewartContact"
                      value={formData.StewartContact}
                      onChange={resetErrors}
                      error={!!errors.StewartContact}
                      errorMsg={errors.StewartContact}
                    />
                  </div>
                  <div
                    className="request-account-form__grid-col"
                    style={{ "--span": 8 }}
                  >
                    <TextInput
                      label="*Stewart Contact Email"
                      name="StewartContactEmail"
                      error={!!errors.StewartContactEmail}
                      errorMsg={errors.StewartContactEmail}
                      value={formData.StewartContactEmail}
                      onChange={resetErrors}
                    />
                  </div>
                  <div
                    className="request-account-form__grid-col"
                    style={{ "--span": 16 }}
                  >
                    <a
                      href={window?.regFormModel?.noContactUrl || "/contact"}
                      className="stewart-contact-link"
                    >
                      Don’t have a Stewart contact?
                    </a>
                  </div>
                </>
              )}
            </div>
            <div className="request-account-form__form-actions">
              {mode === "register" && (
                <>
                  <span>
                    By submitting a request, you agree to the{" "}
                    <a href="/terms-of-use">Terms of Use</a> and{" "}
                    <a href="/privacy">Privacy Policy</a>
                  </span>
                  <button
                    className="btn btn--primary btn--primary--inverted"
                    onClick={handleClear}
                    type="button"
                  >
                    Clear
                  </button>
                </>
              )}
              <button
                className="btn btn--primary"
                type="submit"
                disabled={!canSubmit || isSubmitting ? true : null}
              >
                {mode === "edit"
                  ? initialData
                    ? (initialData.LabelTextUpdateProfile ?? "Update profile")
                    : "Update profile"
                  : initialData
                    ? (initialData.LabelTextSubmitRequest ?? "Submit request")
                    : "Submit request"}
              </button>
            </div>
          </form>
        )}
        {submitResult === "success" && (
          <FormSubmitted
            resultType="success"
            introMessage={window.regFormModel.confirmMsg}
            variant={variant}
          />
        )}
        {submitResult === "pending" && (
          <FormSubmitted
            resultType="pending"
            introMessage={window.regFormModel.pendingMsg}
            variant={variant}
          />
        )}
        {submitResult === "active" && (
          <FormSubmitted
            resultType="active"
            introMessage={window.regFormModel.activeMsg}
            variant={variant}
          />
        )}
        {submitResult === "inactive" && (
          <FormSubmitted
            resultType="inactive"
            introMessage={window.regFormModel.inactiveMsg}
            variant={variant}
          />
        )}
        {isSubmitting && <LoadingScreen />}
      </div>
      <ToastContainer autoClose={false} />
    </>
  );
}
 
export default SSORequestAccountForm;
 